import { useCallback, useEffect, useRef, useState } from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import {
  Eye, GitCompare, Terminal, FolderOpen, ListChecks, NotebookPen,
  ChevronRight, ChevronLeft, Folder, FileCode, FileText, Circle, Check, Clock,
  PanelRightClose, PanelRightOpen, RotateCw, ExternalLink, AlertTriangle,
  Camera, Bot, X, Plus, Square, Columns2, Rows2, LayoutGrid,
  MousePointerSquareDashed,
} from 'lucide-react';
import { useUIStore, INSPECTOR_MIN, LAYOUT_CAPACITY, type Mode, type Pane, type InspectorLayout } from '@/lib/store/uiStore';
import { useSessionStore } from '@/lib/store/sessionStore';
import { useActiveSession } from '@/hooks/useActiveSession';
import { Markdown } from '../thread/markdown/Markdown';
import { cn } from '@/lib/utils';
import { chord } from '@/lib/platform';

/** Resolve the Loom API base, mirroring BackendCliAdapter.resolveAgentBase. */
function resolveLoomApiBase(): string {
  const RELATIVE = '/api/v1';
  if (typeof window === 'undefined') return RELATIVE;
  try {
    const host = new URLSearchParams(window.location.search).get('host');
    if (host && /^https?:\/\//.test(host)) return `${host.replace(/\/$/, '')}${RELATIVE}`;
  } catch { /* noop */ }
  return RELATIVE;
}

const TITLES: Record<Mode, { label: string; icon: React.ComponentType<{ className?: string }> }> = {
  preview:  { label: 'Preview',  icon: Eye },
  diff:     { label: 'Diff',     icon: GitCompare },
  terminal: { label: 'Terminal', icon: Terminal },
  files:    { label: 'Files',    icon: FolderOpen },
  tasks:    { label: 'Tasks',    icon: ListChecks },
  plan:     { label: 'Plan',     icon: NotebookPen },
};

export function InspectorPanel() {
  const open = useUIStore((s) => s.inspectorOpen);
  const setOpen = useUIStore((s) => s.setInspectorOpen);
  const width = useUIStore((s) => s.inspectorWidth);
  const setWidth = useUIStore((s) => s.setInspectorWidth);
  const layout = useUIStore((s) => s.inspectorLayout);
  const setLayout = useUIStore((s) => s.setInspectorLayout);
  const panes = useUIStore((s) => s.inspectorPanes);
  const addPane = useUIStore((s) => s.addPane);
  const activePaneId = useUIStore((s) => s.activePaneId);
  const focusedPane = panes.find((p) => p.id === activePaneId) ?? panes[0];
  const Icon = focusedPane ? TITLES[focusedPane.mode].icon : Eye;
  const dragging = useRef(false);

  const canAddPane = panes.length < LAYOUT_CAPACITY[layout];

  const onPointerDown = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    dragging.current = true;
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, []);

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    if (!dragging.current) return;
    // resize from the right edge of the viewport
    const next = window.innerWidth - e.clientX;
    setWidth(next);
  }, [setWidth]);

  const onPointerUp = useCallback((e: React.PointerEvent) => {
    if (!dragging.current) return;
    dragging.current = false;
    try { (e.target as HTMLElement).releasePointerCapture(e.pointerId); } catch { /* noop */ }
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, []);

  useEffect(() => () => {
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, []);

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        title={`Open inspector (${chord('mod', '\\')})`}
        aria-label="Open inspector"
        className="focus-ring flex h-full w-9 shrink-0 flex-col items-center justify-start gap-2 border-l border-border bg-rail py-3 text-fg-muted hover:bg-surface-2 hover:text-fg"
      >
        <PanelRightOpen className="h-4 w-4" />
        <Icon className="h-3.5 w-3.5" />
      </button>
    );
  }

  return (
    <aside
      style={{ width }}
      className="relative flex h-full shrink-0 flex-col border-l border-border bg-rail"
    >
      {/* drag handle on the left edge */}
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize inspector"
        aria-valuemin={INSPECTOR_MIN}
        aria-valuenow={width}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        className="group absolute -left-1 top-0 z-10 h-full w-2 cursor-col-resize"
      >
        <span className="pointer-events-none absolute inset-y-0 left-1 w-px bg-transparent transition-colors group-hover:bg-accent/40" />
      </div>

      <header className="flex h-11 shrink-0 items-center gap-1 border-b border-border px-2">
        <LayoutSwitcher value={layout} onChange={setLayout} />
        <span className="ml-1 text-2xs uppercase tracking-wide text-fg-subtle">Inspector</span>
        <span className="ml-auto text-2xs tracking-wide text-fg-subtle">{width}px</span>
        {canAddPane && (
          <button
            type="button"
            onClick={() => addPane(focusedPane?.mode === 'preview' ? 'plan' : 'preview')}
            className="focus-ring rounded p-1 text-fg-muted hover:bg-surface-2 hover:text-fg"
            title="Add pane"
            aria-label="Add pane"
          >
            <Plus className="h-3.5 w-3.5" />
          </button>
        )}
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="focus-ring rounded p-1 text-fg-muted hover:bg-surface-2 hover:text-fg"
          aria-label="Hide inspector"
          title={`Hide inspector (${chord('mod', '\\')})`}
        >
          <PanelRightClose className="h-3.5 w-3.5" />
        </button>
      </header>

      <PaneGrid layout={layout} panes={panes} />
    </aside>
  );
}

/* ---------- Layout switcher ---------- */
const LAYOUT_BUTTONS: Array<{ value: InspectorLayout; icon: React.ComponentType<{ className?: string }>; title: string }> = [
  { value: 'single',   icon: Square,     title: 'Single' },
  { value: 'split-h',  icon: Columns2,   title: 'Split horizontal' },
  { value: 'split-v',  icon: Rows2,      title: 'Split vertical' },
  { value: 'grid-2x2', icon: LayoutGrid, title: '2x2 grid' },
];

function LayoutSwitcher({ value, onChange }: { value: InspectorLayout; onChange: (l: InspectorLayout) => void }) {
  return (
    <div className="flex items-center gap-0.5 rounded-md border border-border bg-canvas p-0.5">
      {LAYOUT_BUTTONS.map((b) => {
        const I = b.icon;
        const active = b.value === value;
        return (
          <button
            key={b.value}
            type="button"
            onClick={() => onChange(b.value)}
            title={b.title}
            aria-label={b.title}
            className={cn(
              'focus-ring flex h-5 w-5 items-center justify-center rounded',
              active ? 'bg-fg text-canvas' : 'text-fg-muted hover:bg-surface-2 hover:text-fg'
            )}
          >
            <I className="h-3 w-3" />
          </button>
        );
      })}
    </div>
  );
}

/* ---------- Pane grid ---------- */
function PaneGrid({ layout, panes }: { layout: InspectorLayout; panes: Pane[] }) {
  // Two CSS-grid templates cover everything.
  let gridTemplate: React.CSSProperties;
  if (layout === 'single' || panes.length === 1) {
    gridTemplate = { gridTemplateColumns: '1fr', gridTemplateRows: '1fr' };
  } else if (layout === 'split-h') {
    gridTemplate = { gridTemplateColumns: '1fr 1fr', gridTemplateRows: '1fr' };
  } else if (layout === 'split-v') {
    gridTemplate = { gridTemplateColumns: '1fr', gridTemplateRows: '1fr 1fr' };
  } else {
    gridTemplate = { gridTemplateColumns: '1fr 1fr', gridTemplateRows: '1fr 1fr' };
  }
  return (
    <div
      className="min-h-0 flex-1 grid gap-px bg-border"
      style={gridTemplate}
    >
      {panes.map((p) => (
        <PaneCell key={p.id} pane={p} />
      ))}
    </div>
  );
}

function PaneCell({ pane }: { pane: Pane }) {
  const setPaneMode = useUIStore((s) => s.setPaneMode);
  const removePane = useUIStore((s) => s.removePane);
  const setActivePaneId = useUIStore((s) => s.setActivePaneId);
  const panes = useUIStore((s) => s.inspectorPanes);
  const active = useUIStore((s) => s.activePaneId === pane.id);
  const Icon = TITLES[pane.mode].icon;
  const canClose = panes.length > 1;
  return (
    <section
      onMouseDown={() => setActivePaneId(pane.id)}
      className={cn(
        'flex min-h-0 flex-col bg-rail outline-none',
        active && panes.length > 1 && 'ring-1 ring-accent/30'
      )}
    >
      <header className="flex h-8 shrink-0 items-center gap-1 border-b border-border bg-surface px-2">
        <PaneModeMenu pane={pane} setMode={(m) => setPaneMode(pane.id, m)} />
        {canClose && (
          <button
            type="button"
            onClick={() => removePane(pane.id)}
            className="focus-ring ml-auto rounded p-0.5 text-fg-subtle hover:bg-surface-2 hover:text-fg"
            title="Close pane"
            aria-label="Close pane"
          >
            <X className="h-3 w-3" />
          </button>
        )}
        {!canClose && <Icon className="ml-auto h-3 w-3 text-fg-subtle" />}
      </header>
      <div className="min-h-0 flex-1 overflow-y-auto bg-rail">
        <View mode={pane.mode} />
      </div>
    </section>
  );
}

function PaneModeMenu({ pane, setMode }: { pane: Pane; setMode: (m: Mode) => void }) {
  const Icon = TITLES[pane.mode].icon;
  const allModes: Mode[] = ['preview', 'plan', 'diff', 'files', 'tasks', 'terminal'];
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          className="focus-ring flex items-center gap-1 rounded px-1 py-0.5 text-[12px] text-fg hover:bg-surface-2"
          title="Change pane mode"
        >
          <Icon className="h-3 w-3 text-fg-muted" />
          <span className="font-medium">{TITLES[pane.mode].label}</span>
          <ChevronRight className="h-3 w-3 rotate-90 text-fg-subtle" />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="start"
          sideOffset={4}
          className="z-50 min-w-[160px] rounded-md border border-border bg-canvas p-1 shadow-menu animate-fade-in"
        >
          {allModes.map((m) => {
            const I = TITLES[m].icon;
            return (
              <DropdownMenu.Item
                key={m}
                className="menu-item"
                onSelect={() => setMode(m)}
              >
                <Check className={cn('h-3.5 w-3.5', m === pane.mode ? 'text-accent' : 'opacity-0')} />
                <I className="h-3.5 w-3.5 text-fg-muted" />
                <span>{TITLES[m].label}</span>
              </DropdownMenu.Item>
            );
          })}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

function View({ mode }: { mode: Mode }) {
  switch (mode) {
    case 'preview':  return <PreviewView />;
    case 'diff':     return <DiffView />;
    case 'terminal': return <TerminalView />;
    case 'files':    return <FilesView />;
    case 'tasks':    return <TasksView />;
    case 'plan':     return <PlanView />;
  }
}

/* ---------- Preview ---------- */
function PreviewView() {
  const session = useActiveSession();
  const stored = useSessionStore((s) =>
    session ? s.panelStates[session.id]?.preview : undefined
  );
  const setPanelState = useSessionStore((s) => s.setPanelState);

  // History stack for back/forward — local to the pane, not persisted.
  const [history, setHistory] = useState<string[]>([]);
  const [cursor, setCursor] = useState(-1);
  const [editing, setEditing] = useState('');
  const [iframeKey, setIframeKey] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [errored, setErrored] = useState(false);
  const [errorReason, setErrorReason] = useState<string | null>(null);
  // Pre-flight probe state: while probing, we hold off on rendering the
  // iframe so X-Frame-Options / CSP frame-ancestors blocks (which fire
  // onLoad on the browser's own error page) get caught up front.
  const [probing, setProbing] = useState(false);
  // Select-element tool: when on, we inject an inspector script into the
  // iframe (only same-origin) so the user can click an element to attach
  // it to the composer draft. Cross-origin iframes can't be inspected.
  const [selecting, setSelecting] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const setPendingInsert = useUIStore((s) => s.setPendingComposerInsert);

  // When Claude updates the URL, push it onto history and load it.
  useEffect(() => {
    const url = stored?.url;
    if (!url) return;
    setHistory((h) => {
      // avoid double-push of identical url
      if (h[h.length - 1] === url) return h;
      const next = h.slice(0, cursor + 1);
      next.push(url);
      return next;
    });
    setCursor((c) => c + 1);
    setEditing(url);
    setLoaded(false);
    setErrored(false);
    setErrorReason(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stored?.url]);

  // Pre-validate the URL — file://, javascript:, etc. cannot load in a
  // cross-origin iframe and won't fire onError, so we'd hang forever.
  // Then probe the URL via Loom to detect X-Frame-Options / CSP
  // frame-ancestors blocks, which the browser silently masks with its
  // own error page (onLoad still fires) so client-side detection alone
  // can't see them.
  const currentUrlForValidation = cursor >= 0 ? history[cursor] : '';
  useEffect(() => {
    if (!currentUrlForValidation) return;
    if (/^file:/i.test(currentUrlForValidation)) {
      setErrored(true);
      setErrorReason('file_url');
      return;
    }
    if (/^(javascript|data):/i.test(currentUrlForValidation)) {
      setErrored(true);
      setErrorReason('unsafe_protocol');
      return;
    }

    let cancelled = false;
    setProbing(true);

    // Backstop timeout — only fires if probe didn't conclude AND iframe
    // didn't load. Cleared by probe completion (either result) so a
    // probe-detected block doesn't get overwritten by 'timeout' later.
    let backstop: ReturnType<typeof setTimeout> | null = setTimeout(() => {
      backstop = null;
      setLoaded((curLoaded) => {
        if (!curLoaded) {
          setErrored((curErr) => {
            if (!curErr) setErrorReason('timeout');
            return true;
          });
        }
        return curLoaded;
      });
    }, 10_000);
    const clearBackstop = () => { if (backstop) { clearTimeout(backstop); backstop = null; } };

    const probeUrl = `${resolveLoomApiBase()}/xoro/preview-probe?url=${encodeURIComponent(currentUrlForValidation)}`;
    fetch(probeUrl, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : { embeddable: true })) // probe endpoint failure: fall back to letting iframe try
      .then((data: { embeddable: boolean; reason?: string }) => {
        if (cancelled) return;
        setProbing(false);
        clearBackstop();
        if (!data.embeddable) {
          setErrored(true);
          setErrorReason(data.reason ?? 'embed-blocked');
        }
      })
      .catch(() => {
        if (cancelled) return;
        setProbing(false);
        // Probe failed — let the iframe try. Backstop still armed.
      });

    return () => { cancelled = true; clearBackstop(); };
  }, [currentUrlForValidation, iframeKey]);

  const currentUrl = cursor >= 0 ? history[cursor] : '';

  const navigate = (url: string) => {
    if (!session || !url) return;
    setPanelState(session.id, { preview: { url, title: stored?.title } });
  };

  const reload = () => {
    setIframeKey((k) => k + 1);
    setLoaded(false);
    setErrored(false);
  };

  const goBack = () => { if (cursor > 0) setCursor(cursor - 1); };
  const goFwd = () => { if (cursor < history.length - 1) setCursor(cursor + 1); };

  // Inject / tear down the inspector script when the toggle changes.
  useEffect(() => {
    if (selecting && loaded) injectInspector(iframeRef.current);
    if (!selecting) teardownInspector(iframeRef.current);
  }, [selecting, loaded, iframeKey]);

  // Listen for the inspector script's postMessage with the picked element,
  // build a markdown-ish reference, and stage it for the composer to pick up.
  useEffect(() => {
    if (!selecting) return;
    const onMsg = (ev: MessageEvent) => {
      const data = ev.data as { type?: string; selector?: string; text?: string; html?: string };
      if (!data || data.type !== 'xoro-select-element') return;
      // Belt-and-suspenders: ignore messages from windows we didn't expect.
      if (iframeRef.current && ev.source !== iframeRef.current.contentWindow) return;
      const ref = formatSelectionForComposer(data);
      setPendingInsert(ref);
      setSelecting(false);
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, [selecting, setPendingInsert]);

  // empty state
  if (!currentUrl) {
    return (
      <div className="flex h-full flex-col">
        <PreviewToolbar
          editing={editing}
          setEditing={setEditing}
          onSubmit={() => navigate(editing.trim())}
          onBack={goBack}
          onForward={goFwd}
          onReload={reload}
          canBack={false}
          canForward={false}
          openExternal={null}
        />
        <div className="flex flex-1 items-center justify-center p-6 text-center">
          <div>
            <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-lg bg-surface-2 text-fg-muted">
              <Eye className="h-4 w-4" />
            </div>
            <p className="text-[13px] font-medium text-fg">No URL loaded</p>
            <p className="mt-1 text-[12px] text-fg-muted">
              Type a URL above, or ask Claude to open one — try
              <span className="ml-1 font-mono text-fg">"open preview to localhost:3000"</span>.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <PreviewToolbar
        editing={editing}
        setEditing={setEditing}
        onSubmit={() => navigate(editing.trim())}
        onBack={goBack}
        onForward={goFwd}
        onReload={reload}
        canBack={cursor > 0}
        canForward={cursor < history.length - 1}
        openExternal={currentUrl}
        selecting={selecting}
        onToggleSelect={() => setSelecting((v) => !v)}
      />

      <div className="relative flex-1 bg-canvas">
        {!errored && !probing && (
          <iframe
            ref={iframeRef}
            key={`${currentUrl}-${iframeKey}`}
            src={currentUrl}
            title={stored?.title ?? 'Preview'}
            className="h-full w-full border-0"
            sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-downloads"
            onLoad={() => {
              setLoaded(true);
              // If select-mode is on at load time, re-inject so reloads keep it.
              if (selecting) injectInspector(iframeRef.current);
            }}
            onError={() => setErrored(true)}
          />
        )}
        {(probing || (!loaded && !errored)) && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-canvas/80 text-[12px] text-fg-muted">
            {probing ? 'Checking…' : 'Loading…'}
          </div>
        )}
        {errored && (
          <BlockedStateOverlay
            url={currentUrl}
            reason={errorReason}
            onClear={() => {
              if (!session) return;
              setPanelState(session.id, { preview: undefined });
              setHistory([]);
              setCursor(-1);
              setEditing('');
              setErrored(false);
              setErrorReason(null);
            }}
          />
        )}
      </div>
    </div>
  );
}

function BlockedStateOverlay({ url, reason, onClear }: { url: string; reason: string | null; onClear?: () => void }) {
  const isXfo = reason?.startsWith('x-frame-options');
  const isFa = reason?.startsWith('frame-ancestors');
  const isStale = reason === 'stale-file';
  const isUpstream = reason === 'upstream-error';
  const headline =
    reason === 'file_url'        ? "Can't load file:// URLs in an iframe."
    : reason === 'unsafe_protocol' ? "Can't load this URL scheme."
    : reason === 'fetch-failed'  ? "Couldn't reach this site."
    : reason === 'bad-scheme'    ? "Unsupported URL scheme."
    : reason === 'timeout'       ? "This site didn't load — likely blocked."
    : isStale                    ? "This preview link is stale."
    : isUpstream                 ? "Upstream returned an error."
    : isXfo                      ? "This site refuses to be embedded."
    : isFa                       ? "This site's CSP blocks embedding here."
    : "This site can't be embedded.";
  const explainer =
    reason === 'file_url' ? (
      <>
        Browsers block <code className="rounded bg-surface px-1 py-0.5 font-mono text-[11px]">file://</code> URLs from
        cross-origin iframes. Ask Claude to{' '}
        <span className="font-medium">serve the file via HTTP</span> instead — or open it externally.
      </>
    ) : reason === 'fetch-failed' ? (
      <>
        The probe couldn't fetch the URL (DNS, network, or server error). The site may still load
        directly — try Open externally.
      </>
    ) : isXfo ? (
      <>
        The site sets{' '}
        <code className="rounded bg-surface px-1 py-0.5 font-mono text-[11px]">X-Frame-Options</code>{' '}
        to forbid being embedded. Common for Google, banks, social sites. Try one of these:
      </>
    ) : isFa ? (
      <>
        The site's{' '}
        <code className="rounded bg-surface px-1 py-0.5 font-mono text-[11px]">Content-Security-Policy: frame-ancestors</code>{' '}
        directive doesn't allow this origin. Try one of these:
      </>
    ) : isStale ? (
      <>
        The previously-shown file no longer exists on the server (probably a session
        that's been cleaned up). Clear the preview to dismiss this and pick a fresh URL.
      </>
    ) : isUpstream ? (
      <>
        The target server returned an error response. It may be down, restarting, or
        misconfigured — try again, or open externally to see the real response.
      </>
    ) : reason === 'timeout' ? (
      <>
        Iframe load timed out (10s). Could be a slow network — or a silent embed block.
        Try the alternatives below.
      </>
    ) : (
      <>
        Many sites (Google, banks, social) refuse to load in iframes via{' '}
        <code className="rounded bg-surface px-1 py-0.5 font-mono text-[11px]">X-Frame-Options</code>.
        Try one of these instead:
      </>
    );
  return (
    <div className="absolute inset-0 flex items-center justify-center bg-canvas/95 p-6">
      <div className="w-full max-w-sm">
        <div className="mb-3 flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-warning" />
          <span className="text-[13px] font-medium text-fg">{headline}</span>
        </div>
        <p className="mb-4 text-[12px] leading-relaxed text-fg-muted">
          {explainer}
        </p>
        <div className="space-y-1.5">
          {onClear && (
            <button
              type="button"
              onClick={onClear}
              className="focus-ring flex w-full items-center gap-2 rounded-md border border-border bg-surface px-3 py-2 text-[12.5px] font-medium text-fg hover:border-border-strong hover:bg-surface-2"
            >
              <X className="h-3.5 w-3.5 text-fg-muted" />
              <span className="flex-1 text-left">Clear preview</span>
              <span className="text-2xs text-fg-subtle">dismiss</span>
            </button>
          )}
          <a
            href={url}
            target="_blank"
            rel="noreferrer noopener"
            className="focus-ring flex items-center gap-2 rounded-md border border-border bg-surface px-3 py-2 text-[12.5px] font-medium text-fg hover:border-border-strong hover:bg-surface-2"
          >
            <ExternalLink className="h-3.5 w-3.5 text-fg-muted" />
            <span className="flex-1 text-left">Open externally</span>
            <span className="text-2xs text-fg-subtle">new tab</span>
          </a>
          <button
            type="button"
            disabled
            className="flex w-full items-center gap-2 rounded-md border border-border bg-surface px-3 py-2 text-[12.5px] font-medium text-fg opacity-50 cursor-not-allowed"
            title="Coming in Phase 8a.5 (Playwright server-side capture)"
          >
            <Camera className="h-3.5 w-3.5 text-fg-muted" />
            <span className="flex-1 text-left">Capture screenshot</span>
            <span className="text-2xs text-fg-subtle">8a.5</span>
          </button>
          <button
            type="button"
            disabled
            className="flex w-full items-center gap-2 rounded-md border border-border bg-surface px-3 py-2 text-[12.5px] font-medium text-fg opacity-50 cursor-not-allowed"
            title="Coming in Phase 8b (Chrome DevTools MCP)"
          >
            <Bot className="h-3.5 w-3.5 text-fg-muted" />
            <span className="flex-1 text-left">Open in Browser Agent</span>
            <span className="text-2xs text-fg-subtle">8b</span>
          </button>
        </div>
      </div>
    </div>
  );
}

function PreviewToolbar({
  editing, setEditing, onSubmit, onBack, onForward, onReload, canBack, canForward, openExternal,
  selecting, onToggleSelect,
}: {
  editing: string;
  setEditing: (v: string) => void;
  onSubmit: () => void;
  onBack: () => void;
  onForward: () => void;
  onReload: () => void;
  canBack: boolean;
  canForward: boolean;
  openExternal: string | null;
  selecting?: boolean;
  onToggleSelect?: () => void;
}) {
  return (
    <div className="flex items-center gap-1 border-b border-border bg-surface px-2 py-1.5">
      <button
        type="button"
        onClick={onBack}
        disabled={!canBack}
        title="Back"
        className="focus-ring flex h-6 w-6 items-center justify-center rounded text-fg-muted hover:bg-surface-2 hover:text-fg disabled:opacity-30 disabled:hover:bg-transparent"
      >
        <ChevronLeft className="h-3.5 w-3.5" />
      </button>
      <button
        type="button"
        onClick={onForward}
        disabled={!canForward}
        title="Forward"
        className="focus-ring flex h-6 w-6 items-center justify-center rounded text-fg-muted hover:bg-surface-2 hover:text-fg disabled:opacity-30 disabled:hover:bg-transparent"
      >
        <ChevronRight className="h-3.5 w-3.5" />
      </button>
      <button
        type="button"
        onClick={onReload}
        title="Reload"
        className="focus-ring flex h-6 w-6 items-center justify-center rounded text-fg-muted hover:bg-surface-2 hover:text-fg"
      >
        <RotateCw className="h-3.5 w-3.5" />
      </button>
      <input
        value={editing}
        onChange={(e) => setEditing(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') onSubmit(); }}
        placeholder="https://…"
        className="focus-ring mx-1 flex-1 rounded-md border border-border bg-canvas px-2 py-0.5 font-mono text-[12px] text-fg outline-none placeholder:text-fg-subtle"
      />
      {onToggleSelect && (
        <button
          type="button"
          onClick={onToggleSelect}
          title={selecting ? 'Cancel select element (Esc)' : 'Select element to attach'}
          aria-pressed={!!selecting}
          className={cn(
            'focus-ring flex h-6 w-6 items-center justify-center rounded',
            selecting
              ? 'bg-accent/15 text-accent'
              : 'text-fg-muted hover:bg-surface-2 hover:text-fg',
          )}
        >
          <MousePointerSquareDashed className="h-3.5 w-3.5" />
        </button>
      )}
      {openExternal && (
        <a
          href={openExternal}
          target="_blank"
          rel="noreferrer noopener"
          title="Open in new tab"
          className="focus-ring flex h-6 w-6 items-center justify-center rounded text-fg-muted hover:bg-surface-2 hover:text-fg"
        >
          <ExternalLink className="h-3.5 w-3.5" />
        </a>
      )}
    </div>
  );
}

/* ---------- Diff ---------- */
function DiffView() {
  return (
    <div className="px-3 py-2">
      <div className="mb-2 flex items-center gap-1.5 text-[12px] text-fg-muted">
        <FileCode className="h-3 w-3" />
        <span className="font-mono">packages/api/src/review-policy.ts</span>
        <span className="ml-auto rounded bg-success/10 px-1.5 py-0.5 text-2xs font-medium text-success">
          +4
        </span>
        <span className="rounded bg-danger/10 px-1.5 py-0.5 text-2xs font-medium text-danger">
          −1
        </span>
      </div>
      <pre className="overflow-x-auto rounded-md border border-border bg-canvas p-3 font-mono text-[12px] leading-relaxed">
        <code>{`  export function shouldOpenPlanGate(t: Task) {
-   return matrix[t.risk][t.complexity].includes('plan');
+ }
+ export function shouldOpenPlanGate(t: Task, scope: Scope) {
+   if (scope.autoApproveLowRisk && t.risk === 'low' && t.complexity !== 'large') {
+     return false;
+   }
+   return matrix[t.risk][t.complexity].includes('plan');
  }`}</code>
      </pre>
    </div>
  );
}

/* ---------- Terminal ---------- */
function TerminalView() {
  return (
    <div className="h-full bg-canvas px-3 py-2 font-mono text-[12px] leading-relaxed text-fg">
      <Line prompt>npm test --workspace packages/api</Line>
      <Line muted>{'> @loom/api@0.0.0 test'}</Line>
      <Line muted>{'> vitest run'}</Line>
      <Line>{' RUN  v3.0.5  packages/api'}</Line>
      <Line success>{' ✓ review-policy.test.ts (8)'}</Line>
      <Line success>{' ✓ gates.test.ts (12)'}</Line>
      <Line>{''}</Line>
      <Line>{' Test Files  2 passed (2)'}</Line>
      <Line>{'      Tests  20 passed (20)'}</Line>
      <Line muted>{'   Duration  642ms'}</Line>
      <Line prompt blink>&nbsp;</Line>
    </div>
  );
}

function Line({
  children, prompt, muted, success, blink,
}: {
  children: React.ReactNode;
  prompt?: boolean;
  muted?: boolean;
  success?: boolean;
  blink?: boolean;
}) {
  return (
    <div
      className={cn(
        'whitespace-pre',
        muted && 'text-fg-subtle',
        success && 'text-success'
      )}
    >
      {prompt && <span className="mr-1.5 text-accent">$</span>}
      {children}
      {blink && <span className="ml-0 inline-block h-3 w-1.5 animate-pulse-soft bg-fg align-text-bottom" />}
    </div>
  );
}

/* ---------- Files ---------- */
function FilesView() {
  return (
    <div className="px-2 py-2 text-[12.5px]">
      <Tree>
        <FolderRow name="packages" depth={0} open>
          <FolderRow name="api" depth={1} open>
            <FolderRow name="src" depth={2} open>
              <FileRow name="gates.ts" depth={3} active />
              <FileRow name="review-policy.ts" depth={3} modified />
              <FolderRow name="tasks" depth={3}>
                <FileRow name="update.ts" depth={4} />
                <FileRow name="create.ts" depth={4} />
              </FolderRow>
            </FolderRow>
            <FileRow name="package.json" depth={2} />
          </FolderRow>
          <FolderRow name="db" depth={1}>
            <FileRow name="schema.prisma" depth={2} modified />
          </FolderRow>
        </FolderRow>
        <FolderRow name="apps" depth={0}>
          <FolderRow name="web" depth={1}>
            <FileRow name="layout.tsx" depth={2} />
          </FolderRow>
        </FolderRow>
      </Tree>
    </div>
  );
}

function Tree({ children }: { children: React.ReactNode }) {
  return <div>{children}</div>;
}

function FolderRow({
  name, depth, open, children,
}: {
  name: string;
  depth: number;
  open?: boolean;
  children?: React.ReactNode;
}) {
  return (
    <>
      <div
        className="flex cursor-pointer items-center gap-1.5 rounded-sm px-1.5 py-0.5 text-fg-muted hover:bg-surface-2 hover:text-fg"
        style={{ paddingLeft: 6 + depth * 12 }}
      >
        <ChevronRight className={cn('h-3 w-3 transition-transform', open && 'rotate-90')} />
        <Folder className="h-3.5 w-3.5 text-fg-muted" />
        <span>{name}</span>
      </div>
      {open && children}
    </>
  );
}

function FileRow({
  name, depth, active, modified,
}: {
  name: string;
  depth: number;
  active?: boolean;
  modified?: boolean;
}) {
  return (
    <div
      className={cn(
        'flex cursor-pointer items-center gap-1.5 rounded-sm px-1.5 py-0.5 hover:bg-surface-2',
        active && 'bg-surface-2 text-fg',
        !active && 'text-fg-muted hover:text-fg'
      )}
      style={{ paddingLeft: 6 + depth * 12 + 14 }}
    >
      <FileText className="h-3.5 w-3.5 text-fg-subtle" />
      <span>{name}</span>
      {modified && (
        <span className="ml-auto h-1.5 w-1.5 rounded-full bg-warning" title="Modified" />
      )}
    </div>
  );
}

/* ---------- Tasks ---------- */
function TasksView() {
  const tasks = [
    { id: 'L-241', title: 'Xoro Phase 1 — static UI shell', status: 'in_progress', priority: 'P1' },
    { id: 'L-242', title: 'Xoro Phase 2 — session state', status: 'todo', priority: 'P1' },
    { id: 'L-243', title: 'Xoro Phase 3 — streaming adapter', status: 'todo', priority: 'P2' },
    { id: 'L-159', title: 'Fix Hermes embedded chat', status: 'blocked', priority: 'P1' },
    { id: 'L-160', title: 'Build TUI + patch uvicorn proxy_headers', status: 'blocked', priority: 'P2' },
  ];
  return (
    <ul className="divide-y divide-border/60">
      {tasks.map((t) => (
        <li key={t.id} className="flex items-center gap-2 px-3 py-2 text-[13px] hover:bg-surface-2">
          <StatusGlyph status={t.status} />
          <span className="font-mono text-2xs text-fg-subtle tabular-nums">{t.id}</span>
          <span className="flex-1 truncate text-fg">{t.title}</span>
          <span className="rounded bg-surface px-1.5 py-0.5 text-2xs font-medium text-fg-muted">
            {t.priority}
          </span>
        </li>
      ))}
    </ul>
  );
}

function StatusGlyph({ status }: { status: string }) {
  if (status === 'in_progress') return <Clock className="h-3 w-3 text-info" />;
  if (status === 'blocked') return <Circle className="h-3 w-3 fill-warning text-warning" />;
  if (status === 'completed') return <Check className="h-3 w-3 text-success" />;
  return <Circle className="h-3 w-3 text-fg-subtle" />;
}

/* ---------- Plan ---------- */
function PlanView() {
  const session = useActiveSession();
  const stored = useSessionStore((s) =>
    session ? s.panelStates[session.id]?.plan : undefined
  );
  const markdown = stored?.markdown;

  if (!markdown) {
    return (
      <div className="flex h-full items-center justify-center p-6 text-center">
        <div>
          <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-lg bg-surface-2 text-fg-muted">
            <NotebookPen className="h-4 w-4" />
          </div>
          <p className="text-[13px] font-medium text-fg">No plan yet</p>
          <p className="mt-1 text-[12px] text-fg-muted">
            Ask Claude to <span className="font-mono text-fg">"draft a plan"</span> — it'll appear here.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="px-3 py-3">
      <Markdown text={markdown} />
    </div>
  );
}

/* ---------- Select-element helpers ---------- */

const INSPECTOR_MARKER = '__xoro_inspector__';

/**
 * Inject a small inspector script into the iframe document. Highlights
 * elements on hover, captures the click, and posts the chosen element
 * back to the parent via postMessage. Same-origin only — cross-origin
 * iframes throw on contentDocument access and we silently bail.
 */
function injectInspector(iframe: HTMLIFrameElement | null): void {
  if (!iframe) return;
  let doc: Document | null;
  try { doc = iframe.contentDocument; } catch { return; }
  if (!doc) return;
  // Idempotent: bail if already injected.
  if ((doc as unknown as Record<string, unknown>)[INSPECTOR_MARKER]) return;

  const win = iframe.contentWindow;
  if (!win) return;

  const style = doc.createElement('style');
  style.id = 'xoro-inspector-style';
  style.textContent = `
    .__xoro_hover__ { outline: 2px solid #6366f1 !important; outline-offset: 1px; cursor: crosshair !important; }
    body.__xoro_inspecting__ * { cursor: crosshair !important; }
  `;
  doc.head.appendChild(style);
  doc.body.classList.add('__xoro_inspecting__');

  let hovered: Element | null = null;
  const setHover = (el: Element | null) => {
    if (hovered === el) return;
    if (hovered) hovered.classList.remove('__xoro_hover__');
    hovered = el;
    if (hovered) hovered.classList.add('__xoro_hover__');
  };

  const onMove = (e: Event) => setHover(e.target as Element | null);
  const onClick = (e: Event) => {
    e.preventDefault();
    e.stopPropagation();
    const el = e.target as Element | null;
    if (!el) return;
    const payload = {
      type: 'xoro-select-element',
      selector: cssPath(el),
      text: (el.textContent ?? '').trim().slice(0, 200),
      html: el.outerHTML.slice(0, 1500),
    };
    try { win.parent.postMessage(payload, '*'); } catch { /* noop */ }
  };
  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      try { win.parent.postMessage({ type: 'xoro-select-cancel' }, '*'); } catch { /* noop */ }
    }
  };

  doc.addEventListener('mouseover', onMove, true);
  doc.addEventListener('click', onClick, true);
  doc.addEventListener('keydown', onKey, true);

  // Compute a short, readable CSS path from the element to body.
  function cssPath(el: Element): string {
    const parts: string[] = [];
    let cur: Element | null = el;
    while (cur && cur.nodeType === 1 && parts.length < 6 && cur.tagName !== 'BODY') {
      let sel = cur.tagName.toLowerCase();
      if (cur.id) { sel += `#${cur.id}`; parts.unshift(sel); break; }
      const cls = (cur.getAttribute('class') ?? '').trim().split(/\s+/).filter(Boolean).slice(0, 2);
      if (cls.length) sel += '.' + cls.join('.');
      const parent = cur.parentElement;
      if (parent) {
        const sibs = Array.from(parent.children).filter((c) => c.tagName === cur!.tagName);
        if (sibs.length > 1) sel += `:nth-of-type(${sibs.indexOf(cur) + 1})`;
      }
      parts.unshift(sel);
      cur = cur.parentElement;
    }
    return parts.join(' > ');
  }

  // Persist a teardown handle on the document so the React effect can find it.
  (doc as unknown as Record<string, unknown>)[INSPECTOR_MARKER] = () => {
    setHover(null);
    doc!.removeEventListener('mouseover', onMove, true);
    doc!.removeEventListener('click', onClick, true);
    doc!.removeEventListener('keydown', onKey, true);
    doc!.body.classList.remove('__xoro_inspecting__');
    style.remove();
    delete (doc as unknown as Record<string, unknown>)[INSPECTOR_MARKER];
  };
}

function teardownInspector(iframe: HTMLIFrameElement | null): void {
  if (!iframe) return;
  let doc: Document | null;
  try { doc = iframe.contentDocument; } catch { return; }
  if (!doc) return;
  const fn = (doc as unknown as Record<string, unknown>)[INSPECTOR_MARKER];
  if (typeof fn === 'function') (fn as () => void)();
}

function formatSelectionForComposer(d: { selector?: string; text?: string; html?: string }): string {
  // Compact reference: short blurb + selector + a snippet of HTML in a fenced block.
  const blurb = d.text ? `Selected element: "${d.text}"` : 'Selected element';
  const lines = [blurb];
  if (d.selector) lines.push(`Selector: \`${d.selector}\``);
  if (d.html) lines.push('```html\n' + d.html + '\n```');
  return lines.join('\n');
}
